import { useState, type FormEvent } from 'react';
import { api, errorMessage, type Delivery, type Webhook } from '../lib/api';
import { useAsync } from '../lib/hooks';
import { fmtDateTime } from '../lib/format';
import { ActionButton, CopyButton, ErrorAlert, Field, Modal, PageHeader, Spinner } from '../components/ui';
import { useT } from '../i18n';

const EVENTS = [
  'license.created',
  'license.updated',
  'license.activated',
  'license.deactivated',
  'license.suspended',
  'license.revoked',
  'license.expired',
  'trial.started',
];

export function WebhooksPage() {
  const t = useT();
  const list = useAsync(() => api.get<Webhook[]>('/v1/admin/webhooks'), []);
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [deliveriesOf, setDeliveriesOf] = useState<Webhook | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await list.reload();
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <>
      <PageHeader
        title={t('webhooks.title')}
        subtitle={t('webhooks.subtitle')}
        actions={<button className="btn btn-primary" onClick={() => setCreating(true)}>{t('webhooks.new')}</button>}
      />
      <ErrorAlert error={list.error ?? error} />
      {secret && (
        <div className="alert info">
          <strong>{t('webhooks.secretTitle')}</strong> {t('webhooks.secretNote')}
          <div className="secret-box" style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
            {secret} <CopyButton text={secret} />
          </div>
          <div style={{ marginTop: 8 }}>
            {t('webhooks.verification')} <code>HMAC_SHA256(secret, "&lt;t&gt;.&lt;body&gt;")</code> {t('webhooks.verificationDetail')}{' '}
            <code>x-voxel-signature: t=…,v1=…</code>
          </div>
        </div>
      )}
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('webhooks.colUrl')}</th>
                <th>{t('webhooks.colEvents')}</th>
                <th>{t('common.status')}</th>
                <th>{t('common.created')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data?.length === 0 && <tr><td colSpan={5} className="empty">{t('webhooks.empty')}</td></tr>}
              {list.data?.map((w) => (
                <tr key={w.id}>
                  <td>
                    <div className="mono">{w.url}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{w.description}</div>
                  </td>
                  <td>{w.events.length ? w.events.map((e) => <span key={e} className="tag">{e}</span>) : <span className="muted">{t('webhooks.allEvents')}</span>}</td>
                  <td>{w.enabled ? <span className="badge success">{t('webhooks.enabled')}</span> : <span className="badge">{t('webhooks.paused')}</span>}</td>
                  <td>{fmtDateTime(w.created_at)}</td>
                  <td className="num">
                    <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
                      <button className="btn btn-sm" onClick={() => setDeliveriesOf(w)}>{t('webhooks.deliveries')}</button>
                      <ActionButton className="btn btn-sm" onAction={() => run(() => api.post(`/v1/admin/webhooks/${w.id}/test`))}>{t('webhooks.test')}</ActionButton>
                      <ActionButton className="btn btn-sm" onAction={() => run(() => api.patch(`/v1/admin/webhooks/${w.id}`, { enabled: !w.enabled }))}>
                        {w.enabled ? t('webhooks.pause') : t('webhooks.resume')}
                      </ActionButton>
                      <ActionButton className="btn btn-sm btn-danger" confirm={t('webhooks.deleteConfirm')} onAction={() => run(() => api.del(`/v1/admin/webhooks/${w.id}`))}>
                        {t('common.delete')}
                      </ActionButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {creating && (
        <CreateWebhookModal
          onClose={() => setCreating(false)}
          onCreated={async (s) => {
            setCreating(false);
            setSecret(s);
            await list.reload();
          }}
        />
      )}
      {deliveriesOf && <DeliveriesModal webhook={deliveriesOf} onClose={() => setDeliveriesOf(null)} />}
    </>
  );
}

function CreateWebhookModal({ onClose, onCreated }: { onClose: () => void; onCreated: (secret: string) => Promise<void> }) {
  const t = useT();
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const w = await api.post<Webhook>('/v1/admin/webhooks', { url, description: description || null, events });
      await onCreated(w.secret);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={t('webhooks.newTitle')} onClose={onClose}>
      <ErrorAlert error={error} />
      <form onSubmit={submit} className="stack">
        <Field label={t('webhooks.url')}>
          <input type="url" required placeholder="https://tienda.voxel.example/hooks/licencias" value={url} onChange={(e) => setUrl(e.target.value)} />
        </Field>
        <Field label={t('common.description')}><input value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <Field label={t('webhooks.events')} help={t('webhooks.eventsHelp')}>
          <div className="checks">
            {EVENTS.map((ev) => (
              <label key={ev}>
                <input type="checkbox" checked={events.includes(ev)} onChange={(e) => setEvents(e.target.checked ? [...events, ev] : events.filter((x) => x !== ev))} />
                <code>{ev}</code>
              </label>
            ))}
          </div>
        </Field>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={busy}>{busy && <Spinner />} {t('common.create')}</button>
        </div>
      </form>
    </Modal>
  );
}

function DeliveriesModal({ webhook, onClose }: { webhook: Webhook; onClose: () => void }) {
  const t = useT();
  const list = useAsync(() => api.get<Delivery[]>(`/v1/admin/webhooks/${webhook.id}/deliveries`), [webhook.id]);
  const tone: Record<string, string> = { delivered: 'success', pending: 'warning', failed: 'danger' };
  const label = (s: string) => (s in tone ? t(`webhooks.delivery.${s as 'failed'}`) : s);
  return (
    <Modal title={t('webhooks.deliveriesTitle')} onClose={onClose}>
      <ErrorAlert error={list.error} />
      <div className="table-wrap" style={{ margin: 0 }}>
        <table>
          <thead>
            <tr>
              <th>{t('webhooks.colEvent')}</th>
              <th>{t('common.status')}</th>
              <th className="num">{t('webhooks.colAttempts')}</th>
              <th>{t('common.date')}</th>
            </tr>
          </thead>
          <tbody>
            {list.data?.length === 0 && <tr><td colSpan={4} className="empty">{t('webhooks.noDeliveries')}</td></tr>}
            {list.data?.map((d) => (
              <tr key={d.id}>
                <td><code>{d.event}</code></td>
                <td>
                  <span className={`badge ${tone[d.status] ?? ''}`}>{label(d.status)}</span>
                  {d.last_error && <div className="muted" style={{ fontSize: 12 }}>{d.last_error}</div>}
                </td>
                <td className="num">{d.attempts}</td>
                <td>{fmtDateTime(d.delivered_at ?? d.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
